import { useGetProjects } from 'api-hooks/projects';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { Container, TopContainer } from 'components/styles';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import SalePillStatus from 'legacy-modules/sales/component/status';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import ProjectListTabs from './component/project-list-tab';
import ProjectTable from './component/table';

export default function ProjectList() {
  const { t } = useTranslation();
  const formatDate = useDate();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const _columns = React.useMemo(
    () => [
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        minSize: 150,
        sortName: 'created_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 100,
        cell: ({ getValue }) => <SalePillStatus value={getValue() as string} />,
      },
      {
        header: t('common:code'),
        accessorKey: 'code',
        minSize: 150,
        sortName: 'code',
      },
      {
        header: t('common:project_name'),
        accessorKey: 'name',
        minSize: 250,
        sortName: 'name',
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 250,
        sortName: 'description',
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetProjects,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error.message });
    }
  }, [error]);

  const permission = useGetCrudPermission('projects');

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:project_list')}
          showAdd={permission.create}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.ProjectCreate}
        />

        <ProjectListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              setFilters();
            },
            setStatus,
          }}
        />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <ProjectTable
          project={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
